import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

import {
  getCompanyOverview,
  createCompanyNote,
  updateCompanyNote,
  deleteCompanyNote,
  validateCompanyVat,
  CompanyTimelineItem,
  ViesValidationLog,
} from "../../api/companysService";
import { syncCompanyToQuickBooks } from "../../api/quickbooksService";
import { qbSyncBadgeClass, qbSyncLabel } from "../../util/qbSyncStatus";
import PageCard from "../../components/ui/PageCard";
import { useAuth } from "../../hooks/useAuth";

const linkButtonClass =
  "inline-block rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white no-underline hover:bg-torch-primary-dark";

const formatDateTime = (value: string) =>
  value ? new Date(value).toLocaleString() : "—";

const authorLabel = (author: { username?: string; email?: string } | null) =>
  author?.username || author?.email || "Unknown";

const vatStatusBadge = (valid: boolean | null | undefined, validatedAt?: string | null) => {
  if (valid === true) {
    return (
      <span className="rounded bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800">
        Valid{validatedAt ? ` · ${new Date(validatedAt).toLocaleDateString()}` : ""}
      </span>
    );
  }
  if (valid === false) {
    return (
      <span className="rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-800">
        Invalid{validatedAt ? ` · ${new Date(validatedAt).toLocaleDateString()}` : ""}
      </span>
    );
  }
  return (
    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
      Not validated
    </span>
  );
};

const CompanyOverview = () => {
  const { id }: any = useParams();
  const { canEditCompanies, can } = useAuth();
  const canManageQuickBooks = can("quickbooks.manage");
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [noteBody, setNoteBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [vatInput, setVatInput] = useState("");
  const [validatingVat, setValidatingVat] = useState(false);
  const [vatError, setVatError] = useState<string | null>(null);
  const [syncingQb, setSyncingQb] = useState(false);
  const [qbError, setQbError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const result = await getCompanyOverview(id);
    setData(result);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [id]);

  const company = data?.company;
  const contacts = data?.contacts || [];
  const magazineBookings = data?.magazine_bookings || [];
  const timeline: CompanyTimelineItem[] = data?.timeline || [];
  const viesLogs: ViesValidationLog[] = data?.vies_logs || [];

  useEffect(() => {
    if (company?.vat_number != null) {
      setVatInput(company.vat_number);
    }
  }, [company?.vat_number]);

  const primaryContact = useMemo(() => contacts.find((c: any) => c.is_primary) || null, [contacts]);

  const handleAddNote = async () => {
    const body = noteBody.trim();
    if (!body) return;
    setSubmitting(true);
    setError(null);
    try {
      await createCompanyNote(id, body);
      setNoteBody("");
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to add note.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = async (noteId: number) => {
    const body = editBody.trim();
    if (!body) return;
    setSubmitting(true);
    setError(null);
    try {
      await updateCompanyNote(id, noteId, body);
      setEditingNoteId(null);
      setEditBody("");
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to update note.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm("Delete this note?")) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteCompanyNote(id, noteId);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to delete note.");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (noteId: number, body: string) => {
    setEditingNoteId(noteId);
    setEditBody(body);
  };

  const handleValidateVat = async () => {
    const vat = vatInput.trim();
    if (!vat) {
      setVatError("Enter a VAT number to validate.");
      return;
    }
    setValidatingVat(true);
    setVatError(null);
    try {
      await validateCompanyVat(id, vat);
      await load();
    } catch (e: any) {
      setVatError(e?.response?.data?.message || "VIES validation failed.");
    } finally {
      setValidatingVat(false);
    }
  };

  const handleSyncQuickBooks = async () => {
    setSyncingQb(true);
    setQbError(null);
    try {
      await syncCompanyToQuickBooks(id);
      await load();
    } catch (e: any) {
      setQbError(e?.response?.data?.message || "QuickBooks sync failed.");
    } finally {
      setSyncingQb(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{company?.name || "Company"}</h1>
          <p className="mt-1 text-sm text-gray-500">Company overview</p>
        </div>
        {canEditCompanies && (
          <Link to={`/companys/${id}`} className={linkButtonClass}>
            Edit company
          </Link>
        )}
      </div>

      {!loading && data && (
        <PageCard className="mb-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-sm font-medium text-gray-600">Primary contact</div>
              <div className="mt-1 text-sm text-gray-900">
                {primaryContact
                  ? `${primaryContact.first_name}${primaryContact.last_name ? ` ${primaryContact.last_name}` : ""}`
                  : `${company?.first_name || ""} ${company?.last_name || ""}`.trim() || "—"}
              </div>
              <div className="mt-1 text-sm text-gray-600">
                {primaryContact?.email || company?.primary_email || "—"}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-600">Phone</div>
              <div className="mt-1 text-sm text-gray-900">
                {primaryContact?.phone || company?.phone || "—"}
              </div>
            </div>
            {(company?.address || company?.city || company?.postcode || company?.country) && (
              <div className="sm:col-span-2">
                <div className="text-sm font-medium text-gray-600">Billing address</div>
                <div className="mt-1 whitespace-pre-line text-sm text-gray-900">
                  {[company.address, company.city, company.postcode, company.country]
                    .filter(Boolean)
                    .join("\n")}
                </div>
              </div>
            )}
            {company?.description && (
              <div className="sm:col-span-2">
                <div className="text-sm font-medium text-gray-600">Company profile</div>
                <div className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{company.description}</div>
              </div>
            )}
          </div>
        </PageCard>
      )}

      {!loading && data && (
        <PageCard className="mb-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-medium text-gray-900">VAT (VIES)</h2>
              <p className="mt-1 text-sm text-gray-500">EU VAT number validation via the VIES service.</p>
            </div>
            <div>{vatStatusBadge(company?.vat_valid, company?.vat_validated_at)}</div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="vat-number" className="text-sm font-medium text-gray-600">
                VAT number
              </label>
              <div className="mt-1 flex flex-wrap gap-2">
                <input
                  id="vat-number"
                  type="text"
                  value={vatInput}
                  onChange={(e) => setVatInput(e.target.value)}
                  placeholder="DE123456789"
                  disabled={!canEditCompanies || validatingVat}
                  className="min-w-[200px] flex-1 rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-torch-primary focus:outline-none focus:ring-1 focus:ring-torch-primary disabled:bg-gray-50"
                />
                {canEditCompanies && (
                  <button
                    type="button"
                    onClick={handleValidateVat}
                    disabled={validatingVat || !vatInput.trim()}
                    className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark disabled:opacity-50"
                  >
                    {validatingVat ? "Validating…" : "Validate with VIES"}
                  </button>
                )}
              </div>
              {vatError && <p className="mt-2 text-sm text-red-600">{vatError}</p>}
            </div>

            {company?.vat_registered_name && (
              <div>
                <div className="text-sm font-medium text-gray-600">Registered name</div>
                <div className="mt-1 text-sm text-gray-900">{company.vat_registered_name}</div>
              </div>
            )}
            {company?.vat_registered_address && (
              <div>
                <div className="text-sm font-medium text-gray-600">Registered address</div>
                <div className="mt-1 text-sm text-gray-900">{company.vat_registered_address}</div>
              </div>
            )}
          </div>

          {viesLogs.length > 0 && (
            <div className="mt-6 border-t border-gray-200 pt-4">
              <div className="mb-3 text-sm font-medium text-gray-700">Validation history</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-500">
                      <th className="py-2 pr-4">Date</th>
                      <th className="py-2 pr-4">VAT number</th>
                      <th className="py-2 pr-4">Result</th>
                      <th className="py-2 pr-4">Registered name</th>
                      <th className="py-2">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viesLogs.map((log) => (
                      <tr key={log.id} className="border-b border-gray-100">
                        <td className="py-2 pr-4 text-gray-600">{formatDateTime(log.createdAt)}</td>
                        <td className="py-2 pr-4 font-mono text-gray-900">{log.vat_number}</td>
                        <td className="py-2 pr-4">
                          {log.valid ? (
                            <span className="text-green-700">Valid</span>
                          ) : (
                            <span className="text-red-700" title={log.error_message || undefined}>
                              Invalid
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-gray-600">{log.registered_name || "—"}</td>
                        <td className="py-2 text-gray-600">{authorLabel(log.user)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </PageCard>
      )}

      {!loading && data && (
        <PageCard className="mb-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-medium text-gray-900">QuickBooks</h2>
              <p className="mt-1 text-sm text-gray-500">Customer sync status in QuickBooks Online.</p>
            </div>
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${qbSyncBadgeClass(company?.qb_sync_status)}`}
            >
              {qbSyncLabel(company || {}, "company")}
            </span>
          </div>
          {canManageQuickBooks && (
            <button
              type="button"
              onClick={handleSyncQuickBooks}
              disabled={syncingQb}
              className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark disabled:opacity-50"
            >
              {syncingQb ? "Syncing…" : "Sync customer to QuickBooks"}
            </button>
          )}
          {qbError && <p className="mt-2 text-sm text-red-600">{qbError}</p>}
        </PageCard>
      )}

      <PageCard className="mb-6">
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            {/* <Tab label="Activity" /> */}
            <Tab label={`Contacts (${contacts.length})`} />
            <Tab label={`Bookings (${magazineBookings.length})`} />
          </Tabs>
        </Box>

        {loading ? (
          <p className="text-sm text-gray-500" style={{ paddingTop: 16 }}>Loading...</p>
        ) : !data ? (
          <p className="text-sm text-gray-500" style={{ paddingTop: 16 }}>Not found.</p>
        ) : (
          <div style={{ paddingTop: 16 }}>
            {tab === 5 && (
              <div>
                {canEditCompanies && (
                <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <label htmlFor="new-note" className="mb-2 block text-sm font-medium text-gray-700">
                    Add note
                  </label>
                  <textarea
                    id="new-note"
                    rows={3}
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                    placeholder="Share an update with your team…"
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-torch-primary focus:outline-none focus:ring-1 focus:ring-torch-primary"
                    disabled={submitting}
                  />
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleAddNote}
                      disabled={submitting || !noteBody.trim()}
                      className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark disabled:opacity-50"
                    >
                      {submitting ? "Saving…" : "Add note"}
                    </button>
                    {error && <span className="text-sm text-red-600">{error}</span>}
                  </div>
                </div>
                )}

                {timeline.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-500">No activity yet.</p>
                ) : (
                  <ul className="space-y-4">
                    {timeline.map((item) => (
                      <li key={item.id} className="rounded-lg border border-gray-200 p-4">
                        {item.type === "note" ? (
                          <>
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                              <div className="text-sm text-gray-600">
                                <span className="font-medium text-gray-900">{authorLabel(item.author)}</span>
                                <span className="mx-2 text-gray-300">·</span>
                                <span>{formatDateTime(item.createdAt)}</span>
                                <span className="ml-2 rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                                  Note
                                </span>
                              </div>
                              {(item.can_edit || item.can_delete) && editingNoteId !== item.note_id && (
                                <div className="flex gap-2">
                                  {item.can_edit && (
                                    <button
                                      type="button"
                                      onClick={() => startEdit(item.note_id, item.body)}
                                      className="text-sm text-torch-primary hover:underline"
                                      disabled={submitting}
                                    >
                                      Edit
                                    </button>
                                  )}
                                  {item.can_delete && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteNote(item.note_id)}
                                      className="text-sm text-red-600 hover:underline"
                                      disabled={submitting}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                            {editingNoteId === item.note_id ? (
                              <div>
                                <textarea
                                  rows={3}
                                  value={editBody}
                                  onChange={(e) => setEditBody(e.target.value)}
                                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-torch-primary focus:outline-none focus:ring-1 focus:ring-torch-primary"
                                  disabled={submitting}
                                />
                                <div className="mt-2 flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleSaveEdit(item.note_id)}
                                    disabled={submitting || !editBody.trim()}
                                    className="rounded bg-torch-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-torch-primary-dark disabled:opacity-50"
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingNoteId(null);
                                      setEditBody("");
                                    }}
                                    disabled={submitting}
                                    className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="whitespace-pre-wrap text-sm text-gray-900">{item.body}</p>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="mb-1 text-sm text-gray-600">
                              <span className="font-medium text-gray-900">
                                {authorLabel(item.author)}
                              </span>
                              <span className="mx-2 text-gray-300">·</span>
                              <span>{formatDateTime(item.createdAt)}</span>
                              <span className="ml-2 rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                                Magazine booking
                              </span>
                            </div>
                            <p className="text-sm text-gray-900">{item.summary}</p>
                            <p className="mt-1 text-sm capitalize text-gray-500">Status: {item.status}</p>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {tab === 0 && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm text-gray-600">Contacts linked to this company.</div>
                  <Link to={`/crm/contacts?company_id=${id}`} className={linkButtonClass}>
                    Manage contacts
                  </Link>
                </div>

                {contacts.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-500">No contacts yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 text-gray-500">
                          <th className="py-2 pr-4">Name</th>
                          <th className="py-2 pr-4">Email</th>
                          <th className="py-2 pr-4">Phone</th>
                          <th className="py-2 pr-4">Title</th>
                          <th className="py-2">Primary</th>
                          <th className="py-2">Reminder</th>
                          <th className="py-2 text-right">Record</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((c: any) => (
                          <tr key={c.id} className="border-b border-gray-100">
                            <td className="py-3 pr-4 font-medium text-gray-900">
                              {[c.first_name, c.last_name].filter(Boolean).join(" ")}
                            </td>
                            <td className="py-3 pr-4 text-gray-600">{c.email || "—"}</td>
                            <td className="py-3 pr-4 text-gray-600">{c.phone || "—"}</td>
                            <td className="py-3 pr-4 text-gray-600">{c.job_title || "—"}</td>
                            <td className="py-3 text-gray-600">{c.is_primary ? "Yes" : ""}</td>
                            <td className="py-3 pr-4 text-gray-600">{c.remind_at ? new Date(c.remind_at).toLocaleDateString() : "—"}</td>
                            <td className="py-3 text-right"><Link to={`/crm/contacts/${c.id}`} className="font-medium text-[#5949d1] no-underline hover:underline">View contact</Link></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {tab === 1 && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm text-gray-600">Magazine bookings for this company.</div>
                  <Link to="/magazine/proposals" className={linkButtonClass}>
                    Open proposals
                  </Link>
                </div>

                {magazineBookings.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-500">No magazine bookings yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 text-gray-500">
                          <th className="py-2 pr-4">Publication / Issue</th>
                          <th className="py-2 pr-4">Ad slot</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 pr-4">Sales rep</th>
                          <th className="py-2">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {magazineBookings.map((b: any) => (
                          <tr key={b.id} className="border-b border-gray-100">
                            <td className="py-3 pr-4 text-gray-900">
                              {b.ad_slot?.issue?.publication?.name || "—"}
                              <br />
                              <span className="text-gray-500">{b.ad_slot?.issue?.title || "—"}</span>
                            </td>
                            <td className="py-3 pr-4 text-gray-900">{b.ad_slot?.label || "—"}</td>
                            <td className="py-3 pr-4 capitalize text-gray-600">{b.status}</td>
                            <td className="py-3 pr-4 text-gray-600">{b.sales_rep?.username || b.sales_rep?.email || "—"}</td>
                            <td className="py-3 text-gray-600">{b.createdAt ? new Date(b.createdAt).toLocaleDateString() : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </PageCard>
    </Layout>
  );
};

export default CompanyOverview;
